import { TextField, Grid } from "@material-ui/core";
import { Formik } from "formik";
import moment from "moment";
import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { object, string, array } from "yup";

import {
  editContract as employerEditContract,
  fetchContracts,
} from "../../../actions/employers/contractActions";
import { fetchCostCenters } from "../../../actions/employers/costCenterActions";
import { fetchTeammates } from "../../../actions/employers/teammateActions";
import { editContract as recruiterEditContract } from "../../../actions/recruiters/contractActions";
import { clearModal } from "../../../actions/shared/uiActions";
import { makeGetFilteredContracts } from "../../../selectors/contractSelector";
import { getCurrentIndividual } from "../../../selectors/individualSelectors";
import { getCurrentOrganization } from "../../../selectors/organizationSelectors";
import { buildFullName } from "../../../util/formatHelpers";
import { disabledProps } from "../../../util/formHelpers";
import FieldLabel from "../../employer/JobCasts/view/Description/sections/FieldLabel";
import AllocationsField from "../../employer/Shared/Modals/sections/BillingInformation/AllocationsField";
import TimesheetApproversField from "../../employer/Shared/Modals/sections/BillingInformation/TimesheetApproversField";
import FormContextDatePicker from "../../forms/formik/FormContextInputs/FormContextDatePicker";
import FormContextDropdown from "../../forms/formik/FormContextInputs/FormContextDropdown";
import ActionModalContainer from "../../General/ActionModal/ActionModalContainer";
import ActionModalContent from "../../General/ActionModal/ActionModalContent";
import ActionModalFooter from "../../General/ActionModal/ActionModalFooter";
import ActionModalHeader from "../../General/ActionModal/ActionModalHeader";
import useRouteParamFilters from "../../General/customHooks/useRouteParamFilters";
import useSnackbar from "../../General/useSnackbar";

import CostCenterField from "./sections/CostCenterField";
import TimesheetSubmittersField from "./sections/TimeSheetSubmittersField";

function buildEmployerValidationSchema(requireGlString) {
  return object().shape({
    scheduledEndDate: string().nullable(),
    contractClientIndividualIds: array().of(string()).min(1, "Cannot be blank"),
    costCenterId: string().nullable().required("Cannot be blank"),
    allocations: requireGlString
      ? array().of(
          object().shape({
            type: string().required("Cannot be blank").oneOf(["project", "gl_string"]),
            code: string().required("Cannot be blank")
              .when("type", {
                is: "gl_string",
                then: string().required("Cannot be blank")
                  .test("gl-format", "Must be 7 sections separated by '.' (ex: 004.0000.0000.150000.A000.000.0000)", (val) => /^[^.]+(\.[^.]+){6}$/.test(val)),
              }),
          })
        ).min(1, "Must have at least one allocation")
      : array().of(object()),
  });
}

const recruiterValidationSchema = object().shape({
  contractSupplierIndividuals: array()
    .of(
      object().shape({
        value: string().nullable(),
        label: string().nullable(),
      })
    )
    .min(1, "Must have at least 1 submitter"),
});

const timesheetSubmissionOptions = [
  { label: "My Company", value: "client" },
  { label: "Staffing Firm", value: "supplier" },
];

function EditContractModal({ candidate }) {
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const history = useHistory();
  const location = useLocation();

  const [filters] = useRouteParamFilters({
    history,
    location,
    arrayify: false,
  });

  const currentIndividual = useSelector(getCurrentIndividual);
  const currentOrganization = useSelector(getCurrentOrganization);

  const getContracts = useMemo(makeGetFilteredContracts, []);
  const contract = useSelector((state) =>
    getContracts(state, { candidateId: candidate.id })
  )[0];

  const candidateName = `${candidate.attributes.firstName} ${candidate.attributes.lastName}`;

  const contractClientIndividualIds =
    contract.attributes.primaryClientIndividuals.map(
      (individual) => individual.id
    );
  const { costCenterId } = contract.attributes;

  const contractSupplierIndividuals = (
    contract.attributes.supplierIndividuals ?? []
  ).map(({ id, firstName, lastName }) => ({
    value: id,
    label: buildFullName(firstName, lastName),
  }));

  const employer = currentIndividual.type === "employer";
  const requireGlString = currentOrganization.requirePoNumber;
  const defaultTimesheetSubmissionParty = contract.attributes
    .hasEmployerSubmittedTimesheets
    ? "client"
    : "supplier";
  const hasSubmittedPayPeriods = Boolean(
    contract.attributes.hasSubmittedPayPeriods
  );
  const isWfmIntegrationContract = Boolean(contract.attributes.ukgPersonNumber);
  const canEditTimesheetSubmissionParty =
    employer && !hasSubmittedPayPeriods && !isWfmIntegrationContract;

  const validationSchema = employer
    ? buildEmployerValidationSchema(requireGlString)
    : recruiterValidationSchema;

  const onSubmit = (values, { setSubmitting }) => {
    const editContract = employer
      ? employerEditContract
      : recruiterEditContract;

    const data = {
      scheduled_end_date: values.scheduledEndDate,
      contract_client_individual_ids: values.contractClientIndividualIds,
      contract_supplier_individual_ids: values.contractSupplierIndividuals.map(
        ({ value }) => value
      ),
      cost_center_id: values.costCenterId,
      allocations: values.allocations,
    };

    if (values.timesheetSubmissionParty) {
      data.timesheet_submission_party = values.timesheetSubmissionParty;
    }

    dispatch(
      editContract(contract.attributes.recruiterSubmissionId, contract.id, data)
    )
      .then(() => {
        dispatch(clearModal());
        dispatch(fetchTeammates());
        dispatch(fetchContracts(filters));
        dispatch(fetchCostCenters());

        snackbar.showMessage("Contract successfully saved");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        scheduledEndDate: moment(contract.attributes.scheduledEndDate)
          .utc()
          .format("YYYY-MM-DD"),
        contractClientIndividualIds,
        contractSupplierIndividuals,
        costCenterId,
        timesheetSubmissionParty: defaultTimesheetSubmissionParty,
        allocations: contract.attributes.allocations || [{ code: "", type: "project" }],
      }}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, values, ...props }) => (
        <ActionModalContainer color="blue">
          <ActionModalHeader title="Edit Contract" subtitle={candidateName} />
          <ActionModalContent>
            <p style={{ margin: "0 0 24px 0" }}>
              Use this form to edit fields on an open contract. Only identifying
              information and fields that can be edited will appear here.
            </p>
            <div style={{ width: "100%", overflow: "hidden" }}>
              <Grid item xs={3}>
                <TextField
                  {...disabledProps(true)}
                  label={<FieldLabel name="Worker Name" />}
                  value={candidateName}
                />
              </Grid>
              <Grid container style={{ marginBottom: 40 }}>
                <Grid item xs={4}>
                  {employer ? (
                    <>
                      <FormContextDatePicker
                        id="scheduledEndDate"
                        label="Tentative End Date"
                        inputLabelProps={{ shrink: true }}
                        inputProps={{ style: { height: 23 } }}
                      />
                      <CostCenterField />
                      {canEditTimesheetSubmissionParty && (
                        <FormContextDropdown
                          id="timesheetSubmissionParty"
                          label="Timesheet Submission Responsibility"
                          options={timesheetSubmissionOptions}
                          allowEmpty={false}
                          noShrink
                          overrideStyle={{ marginTop: 24, width: "100%" }}
                        />
                      )}
                    </>
                  ) : (
                    <TextField
                      {...disabledProps(true)}
                      label="Tentative End Date"
                      value={moment(contract.attributes.scheduledEndDate)
                        .utc()
                        .format("MMM Do, YYYY")}
                    />
                  )}
                </Grid>
                {employer ? (
                  <Grid item xs={6} style={{ marginLeft: 20 }}>
                    <TimesheetApproversField
                      shouldIncludeAllTimesheetApprovers={true}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={6} style={{ marginLeft: 20 }}>
                    <TimesheetSubmittersField />
                  </Grid>
                )}
              </Grid>
              {employer && requireGlString && (
                <Grid container style={{ marginBottom: 40 }}>
                  <Grid item xs={12}>
                    <AllocationsField />
                  </Grid>
                </Grid>
              )}
            </div>
          </ActionModalContent>
          <ActionModalFooter
            actionText="Save"
            handleAction={() => {
              handleSubmit();
            }}
            isSubmitting={props.isSubmitting}
          />
        </ActionModalContainer>
      )}
    </Formik>
  );
}

export default EditContractModal;
